import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

/* --------------------------------- Distributor Recon Report ------------------------------ */

interface BrandMetrics {
  voucherRequests: number;
  vouchers: number;
  orderValue: number;
  currency: string;
}

interface ReconReport {
  counts: {
    success: number;
    in_progress: number;
    failed: number;
  };
  brands: {
    distributor: { [key: string]: BrandMetrics };
    fincentive: { [key: string]: BrandMetrics };
  };
  distributorOrderValue: number;
  voucherCreationValue: number;
}


export const fetchReconReport = createAsyncThunk<ReconReport, { distributorId: number; fromDate: string; toDate: string }, { rejectValue: string }>("distributor/fetchReconReport", async ({ distributorId, fromDate, toDate }, { rejectWithValue }) => {
  const token = localStorage.getItem("accessToken");
  try {
    const payload = {
      distributorId,
      fromDate,
      toDate,
    };

    const response = await axios.request({
      baseURL: `${window.env.API_URL}/v1/utility/recon/distributor`,
      timeout: 100000,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      data: payload,
      method: "POST",
    });

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || "Failed to fetch Recon Report");
  }
});

/* --------------------------------- Distributor Listing ------------------------------ */


interface Distributor {
  distributorId: number;
  name: string;
}

interface DistributorResponse {
  count: number;
  rows: Distributor[];
}



export const fetchDistributors = createAsyncThunk<DistributorResponse, void, { rejectValue: string }>("distributor/fetchDistributors", async (_, { rejectWithValue }) => {
  const token = localStorage.getItem("accessToken");

  try {
    const response = await axios.get(`${window.env.API_URL}/v1/utility/recon/distributor`, {
      timeout: 10000,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    return response.data; // Assuming the response matches the DistributorResponse type
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || "Failed to fetch distributors");
  }
});

/* --------------------------------- Main State ------------------------------ */

interface DistributorState {
  loading: boolean;
  error: string | null;
  reconReport: ReconReport | null;
  distributors: Distributor[] | null;
  distributorLoading: boolean; 
  distributorError: string | null; 
}

const initialState: DistributorState = {
  loading: false,
  error: null,
  reconReport: null,
  distributors: null,
  distributorLoading: false, 
  distributorError: null, 
};

const distributorReconSlice = createSlice({
  name: "distributor_recon",
  initialState,
  reducers: {
    clearReconReport(state) {
      state.reconReport = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // Distributor Recon Report
      .addCase(fetchReconReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReconReport.fulfilled, (state, action: PayloadAction<ReconReport>) => {
        state.loading = false;
        state.reconReport = action.payload;
      })
      .addCase(fetchReconReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch Recon Report";
      })

      // Distributor Listing
      .addCase(fetchDistributors.pending, (state) => {
        state.distributorLoading = true;
        state.distributorError = null;
      })
      .addCase(fetchDistributors.fulfilled, (state, action: PayloadAction<DistributorResponse>) => {
        state.distributorLoading = false;
        state.distributors = action.payload.rows;
      })
      .addCase(fetchDistributors.rejected, (state, action) => {
        state.distributorLoading = false;
        state.distributorError = action.payload || "Failed to fetch distributors";
      });
  },
});

export const { clearReconReport } = distributorReconSlice.actions;

export default distributorReconSlice.reducer;
